/* eslint-disable no-unsafe-optional-chaining */
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Button } from 'primereact/button';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Markdown from 'markdown-to-jsx';
import GPSummary from '../../../assets/gamePlan/gp_summary.png';
import {
    useGetGameplanCASummaryQuery,
    useGetGameplanConversationSummaryQuery,
    useGetGameplanScorecardSummaryQuery,
    useGetGameplanStrengthSummaryQuery,
} from '../gamePlanSlice';
import { getMonthName } from '../../../utils/dateFormat';
import ReadConversationLog from '../../coaching/conversationLogs/readConversationLog/ReadConversationLog';
import NewConversationLog from '../../coaching/conversationLogs/newConversationLog/NewConversationLog';
import CoachingShoutOut from '../../../components/coachingShoutOut/coachingShoutOUt';
import { CoachingEntityTypes, CoachingTabs } from '../../../utils/typeConstants';
import { selectCurrentUser } from '../../../pages/login/authSlice';
import { useGetAIGameplanSummaryMutation } from '../../../components/vertexAI/vertexAISlice';
import { LoadingSpinner } from '../../../components/loadingSpinner/loadingspinner';

interface VertexAISummary {
    setDisplayModal?: (e: boolean) => void;
    displayModal: boolean;
    authorId: string;
    seletedRowData: any;
}
interface GameplanInput {
    scorecardData?: any;
    correctiveActions?: any;
    coachingStrength?: any;
    coachingConversation?: any;
}

export const GameplanVertexAISummary = (props: VertexAISummary) => {
    const [AIGameplanSummary] = useGetAIGameplanSummaryMutation();
    const loggedEmpUserName = useSelector(selectCurrentUser);
    const [gamePlanRow, setGamePlanRow] = useState<any>();
    const [shoutoutVisible, setShoutoutVisible] = useState(false);
    const [showNewConversationModal, setshowNewConversationModal] = useState(false);
    const [viewState, setViewState] = useState<string>('new');
    const [aiResponse, setAIResponse] = useState<string>('');
    const [inputJsonText, setInputJsonText] = useState<any>({});

    const { data: gameplanScorecardSummaryData, isFetching: isScorecardFetching } =
        useGetGameplanScorecardSummaryQuery(
            {
                employeeNumber: props?.seletedRowData?.employee_number,
                year: props?.seletedRowData?.year ?? new Date().getFullYear(),
                month: props?.seletedRowData?.month ?? new Date().getMonth() + 1,
                intervalMonthsBack: 2,
            },
            {
                skip: !props?.seletedRowData?.employee_number,
            }
        );

    const { data: gameplanCASummaryData, isFetching: isCAFetching } = useGetGameplanCASummaryQuery(
        {
            employeeNumber: props?.seletedRowData?.employee_number,
            year: props?.seletedRowData?.year ?? new Date().getFullYear(),
            month: props?.seletedRowData?.month ?? new Date().getMonth() + 1,
            intervalMonthsBack: 2,
        },
        {
            skip: !props?.seletedRowData?.employee_number,
        }
    );

    const { data: gameplanStrengthSummaryData, isFetching: isStrengthFetching } =
        useGetGameplanStrengthSummaryQuery(
            {
                employeeNumber: props?.seletedRowData?.employee_number,
                year: props?.seletedRowData?.year ?? new Date().getFullYear(),
                month: props?.seletedRowData?.month ?? new Date().getMonth() + 1,
                intervalMonthsBack: 2,
            },
            {
                skip: !props?.seletedRowData?.employee_number,
            }
        );

    const { data: gameplanConversationSummaryData, isFetching: isConversationFetching } =
        useGetGameplanConversationSummaryQuery(
            {
                employeeNumber: props?.seletedRowData?.employee_number,
                year: props?.seletedRowData?.year ?? new Date().getFullYear(),
                month: props?.seletedRowData?.month ?? new Date().getMonth() + 1,
                intervalMonthsBack: 2,
            },
            {
                skip: !props?.seletedRowData?.employee_number,
            }
        );

    const aiGameplanSummaryRequest = () => {
        if (inputJsonText && Object.keys(inputJsonText).length !== 0) {
            const data = {
                inputJson: JSON.stringify(inputJsonText),
                maxLength: 500,
            };
            AIGameplanSummary(data)
                .unwrap()
                .then((res: any) => {
                    if (res) {
                        setAIResponse(res?.response);
                    }
                })
                .then((error: any) => {
                    console.log(error);
                });
        }
    };

    useEffect(() => {
        const inputText: GameplanInput = {};

        if (gameplanScorecardSummaryData?.data?.length > 0) {
            inputText.scorecardData = gameplanScorecardSummaryData.data;
        }

        if (gameplanCASummaryData?.data?.length > 0) {
            inputText.correctiveActions = gameplanCASummaryData.data;
        }

        if (gameplanStrengthSummaryData?.data?.length > 0) {
            inputText.coachingStrength = gameplanStrengthSummaryData.data;
        }

        if (gameplanConversationSummaryData?.data?.length > 0) {
            inputText.coachingConversation = gameplanConversationSummaryData.data;
        }

        if (Object.keys(inputText).length > 0) {
            if (!isScorecardFetching && !isCAFetching && !isStrengthFetching && !isConversationFetching) {
                setInputJsonText(inputText);
            }
        }
    }, [
        gameplanScorecardSummaryData,
        gameplanCASummaryData,
        gameplanStrengthSummaryData,
        gameplanConversationSummaryData,
    ]);

    useEffect(() => {
        if (Object.keys(inputJsonText).length > 0) {
            aiGameplanSummaryRequest();
        }
    }, [inputJsonText]);

    useEffect(() => {
        if (props?.seletedRowData) {
            setGamePlanRow(props?.seletedRowData);
        }
    }, [props?.seletedRowData]);

    const handleActionsClick = (actionName: string) => {
        if (actionName === 'ShoutOut') {
            setShoutoutVisible(true);
        } else if (actionName === 'Discussion') {
            setViewState(gamePlanRow?.discussions ? 'read' : 'new');
            setshowNewConversationModal(true);
        }
    };

    const createButtonsTemplate = () => {
        const buttons = [
            {
                actionName: 'Discussion',
                type: 'Discussion',
                color: 'var(--medium-blue)',
                borderColor: '#3299ff',
                actionCreated: gamePlanRow?.discussions,
                title: gamePlanRow?.discussions ? 'Discussion Created' : 'No Discussion created',
            },
            {
                actionName: 'ShoutOut',
                type: 'Shout Out',
                color: 'var(--dark-orange)',
                borderColor: '#f15523',
                actionCreated: gamePlanRow?.shoutouts,
                title: gamePlanRow?.shoutouts ? 'Shout Out Created' : 'No Shout Out created',
            },
        ];

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', float: 'right' }}>
                {buttons.map((btn, index) => {
                    return (
                        <Button
                            key={`${btn.actionName}-${index}`}
                            onClick={() => handleActionsClick(btn.actionName)}
                            className="p-button-outlined"
                            style={{
                                height: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 12px',
                                borderRadius: '30px',
                                borderColor: btn.borderColor,
                                color: btn.color,
                            }}
                            title={btn.title}
                        >
                            <i className={btn.actionCreated ? 'pi pi-eye' : 'pi pi-plus-circle'} />
                            <Divider
                                style={{
                                    height: '21px',
                                    margin: '0 0px',
                                    width: '10px',
                                    transform: 'rotate(270deg)',
                                }}
                            />
                            <span style={{ fontWeight: 600 }}>{btn.type}</span>
                        </Button>
                    );
                })}
            </div>
        );
    };

    const renderDiscussionModal = () => {
        if (viewState === 'read') {
            return (
                <ReadConversationLog
                    setViewState={setViewState}
                    setDisplayModal={setshowNewConversationModal}
                    viewState={viewState}
                    displayModal={showNewConversationModal}
                    authorId={props?.authorId}
                    authorName={loggedEmpUserName}
                    recieverId={gamePlanRow?.employee_number}
                    inputYear={gamePlanRow.year}
                    inputMonth={gamePlanRow.month}
                    receiverName={gamePlanRow?.employee_name}
                    via={CoachingTabs.GamePlan}
                    coachingEntityId={gamePlanRow?.gameplan_id}
                    isRecipient
                />
            );
        } else if (viewState === 'new') {
            return (
                <NewConversationLog
                    setViewState={setViewState}
                    setDisplayModal={setshowNewConversationModal}
                    viewState={viewState}
                    displayModal={showNewConversationModal}
                    authorId={props?.authorId}
                    authorName={loggedEmpUserName}
                    recieverId={gamePlanRow?.employee_number}
                    receiverName={gamePlanRow?.employee_name}
                    via={CoachingTabs.GamePlan}
                    date={new Date()}
                    coachingEntityId={gamePlanRow?.gameplan_id}
                    coachingEntityTypeId={CoachingEntityTypes.Gameplan}
                />
            );
        }
        return '';
    };
    const renderShuotOutModal = () => {
        return (
            <CoachingShoutOut
                receiverId={gamePlanRow?.employee_number}
                receiverName={gamePlanRow?.employee_name}
                visible={shoutoutVisible}
                setVisible={setShoutoutVisible}
                coachingEntityId={gamePlanRow?.gameplan_id}
                coachingEntityTypeId={CoachingEntityTypes.Gameplan}
                coreValueId={0}
                via={CoachingTabs.GamePlan}
            />
        );
    };

    const getReviewPeriod = (
        month: number | string | null | undefined,
        year: number | string | null | undefined
    ): string => {
        const now = new Date();
        const monthNum = month ? Number(month) : now.getMonth() + 1;
        const yearNum = year ? Number(year) : now.getFullYear();

        const validYear = !isNaN(yearNum) ? yearNum : now.getFullYear();

        const startMonth = monthNum - 2;
        const startYear = startMonth <= 0 ? validYear - 1 : validYear;
        const adjustedStartMonth = startMonth <= 0 ? 12 + startMonth : startMonth;

        // Format output
        if (monthNum <= 2) {
            return `${getMonthName(adjustedStartMonth)} ${startYear} - ${getMonthName(
                monthNum
            )} ${validYear}`;
        }

        return `${getMonthName(adjustedStartMonth)} - ${getMonthName(monthNum)} ${validYear}`;
    };

    const renderHeader = () => {
        return (
            <div>
                <div className="row pt-3" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div className="col-2">
                        <img style={{ width: '150px', height: '150px' }} alt="Avatar" src={GPSummary} />
                    </div>
                    <div
                        className="col-4"
                        style={{ display: 'flex', flexDirection: 'column', marginTop: '40px' }}
                    >
                        <h4>{gamePlanRow?.employee_name}</h4>
                        <h5 style={{ paddingLeft: '2px' }}>
                            Review Period: {getReviewPeriod(gamePlanRow?.month, gamePlanRow?.year)}
                        </h5>
                    </div>
                    <div className="col-5" style={{ marginTop: '40px' }}>
                        {createButtonsTemplate()}
                    </div>
                </div>
                <Divider />
            </div>
        );
    };

    return (
        <Dialog
            header={renderHeader()}
            visible={props?.displayModal}
            onHide={() => props.setDisplayModal && props.setDisplayModal(false)}
            style={{
                width: '66vw',
                height: '90vh',
            }}
            position="bottom"
            className="MetricDialog card shadow bg-body rounded mt-4 border-0 w-65"
            modal
            draggable={false}
            resizable={false}
        >
            <div>{aiResponse?.length === 0 ? <LoadingSpinner /> : ''}</div>
            <div style={{ fontWeight: 400 }}>
                <Markdown>{aiResponse}</Markdown>
            </div>
            {showNewConversationModal && renderDiscussionModal()}
            {shoutoutVisible && renderShuotOutModal()}
        </Dialog>
    );
};
